import { useMemo, useState, type FormEvent, type JSX } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type TrialVisitDto } from '@ctms/contracts';
import { isHTTPError } from 'ky';
import { AlertCircle, X } from 'lucide-react';

import { getApiErrorMessage } from '../../../api/client.js';
import { trialsApi } from '../trials.api.js';
import { trialQueryKeys } from '../trials.query-keys.js';

/**
 * A4b (#40): manager of trial visit groups — list with usage counts,
 * create, inline rename, delete. Decyzja #40-A4 (2): delete has NO cascade —
 * the API answers 409 `TRIAL_VISIT_GROUP_IN_USE` while visits are attached
 * and the server message is surfaced verbatim in the error banner.
 */

export interface VisitGroupsDialogProps {
  trialId: number;
  /** Catalog rows — used for the per-group usage counts shown in the list. */
  visits: TrialVisitDto[];
  onClose: () => void;
}

/**
 * #44 A-colors: client-side only palette. The API contract deliberately does not
 * expose `trial_visit_groups.color` in v1 (decyzja #39) — a group's colour is
 * derived from its position in the `listVisitGroups` order (orderBy id), which
 * keeps it stable across renders and identical in the catalog and this manager.
 */
export const GROUP_COLORS = [
  '#38bdf8',
  '#fb7185',
  '#a3e635',
  '#fbbf24',
  '#c084fc',
  '#2dd4bf',
  '#f472b6',
  '#818cf8',
] as const;

export function groupColor(
  groups: ReadonlyArray<{ id: number }>,
  groupId: number | null,
): string | null {
  if (groupId === null) {
    return null;
  }
  const index = groups.findIndex((group) => group.id === groupId);
  if (index < 0) {
    return null;
  }
  return GROUP_COLORS[index % GROUP_COLORS.length] ?? null;
}

export interface VisitGroupSwatchProps {
  color: string;
  name: string;
}

/** Colour chip standing for a visit group — shared by the catalog and this manager. */
export function VisitGroupSwatch({ color, name }: VisitGroupSwatchProps): JSX.Element {
  return (
    <span
      data-testid="visit-group-swatch"
      title={name}
      aria-hidden="true"
      className="inline-block h-3 w-3 shrink-0 rounded-sm"
      style={{ backgroundColor: color }}
    />
  );
}

const fieldClass =
  'mt-1.5 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:border-cyan-500 focus:outline-none disabled:opacity-50';

const labelClass = 'block text-xs font-medium uppercase tracking-wider text-slate-300';

export function VisitGroupsDialog({ trialId, visits, onClose }: VisitGroupsDialogProps): JSX.Element {
  const queryClient = useQueryClient();
  const [newName, setNewName] = useState('');
  const [renamingId, setRenamingId] = useState<number | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const groupsQuery = useQuery({
    queryKey: trialQueryKeys.visitGroups(trialId),
    queryFn: () => trialsApi.listVisitGroups(trialId),
    staleTime: 5 * 60 * 1000,
    retry: (retryCount, err) => (isHTTPError(err) ? false : retryCount < 3),
  });
  const groups = groupsQuery.data ?? [];

  const usageById = useMemo(() => {
    const counts = new Map<number, number>();
    for (const visit of visits) {
      if (visit.groupId !== null) {
        counts.set(visit.groupId, (counts.get(visit.groupId) ?? 0) + 1);
      }
    }
    return counts;
  }, [visits]);

  const refreshGroups = (): void => {
    void queryClient.invalidateQueries({ queryKey: trialQueryKeys.visitGroups(trialId) });
  };

  const handleCreate = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const name = newName.trim();
    if (name.length === 0) {
      setErrorMessage('Nazwa grupy nie może być pusta.');
      return;
    }
    setErrorMessage(null);
    setIsSubmitting(true);
    try {
      await trialsApi.createVisitGroup(trialId, { name });
      setNewName('');
      refreshGroups();
    } catch (err: unknown) {
      setErrorMessage(await getApiErrorMessage(err, 'Nie udało się utworzyć grupy.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const startRename = (groupId: number, currentName: string): void => {
    setErrorMessage(null);
    setRenamingId(groupId);
    setRenameValue(currentName);
  };

  const handleRename = async (groupId: number): Promise<void> => {
    const name = renameValue.trim();
    if (name.length === 0) {
      setErrorMessage('Nazwa grupy nie może być pusta.');
      return;
    }
    setErrorMessage(null);
    setIsSubmitting(true);
    try {
      await trialsApi.updateVisitGroup(trialId, groupId, { name });
      setRenamingId(null);
      refreshGroups();
    } catch (err: unknown) {
      setErrorMessage(await getApiErrorMessage(err, 'Nie udało się zapisać zmiany nazwy.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (groupId: number): Promise<void> => {
    setErrorMessage(null);
    setIsSubmitting(true);
    try {
      await trialsApi.deleteVisitGroup(trialId, groupId);
      refreshGroups();
    } catch (err: unknown) {
      // 409 IN_USE keeps the group on the list — the banner carries the
      // server's instruction to move the visits first.
      setErrorMessage(await getApiErrorMessage(err, 'Nie udało się usunąć grupy.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="visit-groups-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4"
    >
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl border border-slate-800 bg-slate-900 p-6 text-slate-100 shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-800 pb-4">
          <div>
            <h2 id="visit-groups-title" className="text-base font-semibold text-slate-100">
              Zarządzaj grupami wizyt
            </h2>
            <p className="mt-1 text-xs text-slate-400">
              Grupa z przypisanymi wizytami nie zostanie usunięta — najpierw przenieś wizyty.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            aria-label="Zamknij"
            className="rounded-md p-1.5 text-slate-400"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {errorMessage !== null && (
          <div
            role="alert"
            className="mt-4 flex items-center gap-2 rounded-md border border-rose-800/60 bg-rose-950/40 p-3 text-sm text-rose-300"
          >
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}

        <form onSubmit={(event) => void handleCreate(event)} className="mt-5" noValidate>
          <label htmlFor="group-new-name" className={labelClass}>Nowa grupa</label>
          <div className="mt-1.5 flex items-start gap-2">
            <input
              id="group-new-name"
              type="text"
              disabled={isSubmitting}
              className={fieldClass}
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className="mt-1.5 shrink-0 rounded-md bg-cyan-600 px-3.5 py-2 text-xs font-medium text-white hover:bg-cyan-500 disabled:opacity-50"
            >
              DODAJ GRUPĘ
            </button>
          </div>
        </form>

        <div className="mt-5">
          {groupsQuery.isLoading ? (
            <p className="py-4 text-sm text-slate-400">Ładowanie grup</p>
          ) : groups.length === 0 ? (
            <p className="py-4 text-sm text-slate-400">Brak grup — dodaj pierwszą grupę powyżej.</p>
          ) : (
            <div className="overflow-hidden rounded-lg border border-slate-800">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-slate-800 bg-slate-950/60 text-slate-400">
                    <th scope="col" className="px-3 py-2">Nazwa</th>
                    <th scope="col" className="px-3 py-2">Wizyty</th>
                    <th scope="col" className="px-3 py-2">Działania</th>
                  </tr>
                </thead>
                <tbody data-testid="visit-groups-body">
                  {groups.map((group) => (
                    <tr key={group.id} className="border-b border-slate-800/80">
                      <td className="px-3 py-2 text-slate-200">
                        {renamingId === group.id ? (
                          <span className="flex items-center gap-2">
                            <input
                              type="text"
                              aria-label={`Nowa nazwa grupy ${group.name}`}
                              disabled={isSubmitting}
                              className="w-full rounded-md border border-slate-700 bg-slate-950 px-2 py-1 text-xs text-slate-100 focus:border-cyan-500 focus:outline-none"
                              value={renameValue}
                              onChange={(event) => setRenameValue(event.target.value)}
                            />
                            <button
                              type="button"
                              disabled={isSubmitting}
                              onClick={() => void handleRename(group.id)}
                              className="shrink-0 rounded-md bg-cyan-600 px-2 py-1 text-xs font-medium text-white hover:bg-cyan-500 disabled:opacity-50"
                            >
                              ZAPISZ
                            </button>
                            <button
                              type="button"
                              disabled={isSubmitting}
                              onClick={() => setRenamingId(null)}
                              className="shrink-0 rounded-md border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800 disabled:opacity-50"
                            >
                              ANULUJ
                            </button>
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            <VisitGroupSwatch
                              color={groupColor(groups, group.id) ?? GROUP_COLORS[0]}
                              name={group.name}
                            />
                            <span>{group.name}</span>
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-400">{usageById.get(group.id) ?? 0}</td>
                      <td className="px-3 py-2">
                        {renamingId === group.id ? null : (
                          <>
                            <button
                              type="button"
                              disabled={isSubmitting}
                              onClick={() => startRename(group.id, group.name)}
                              className="rounded-md border border-slate-700 px-2 py-1 text-xs text-slate-200 hover:bg-slate-800 disabled:opacity-50"
                            >
                              ZMIEŃ NAZWĘ
                            </button>
                            <button
                              type="button"
                              disabled={isSubmitting}
                              onClick={() => void handleDelete(group.id)}
                              className="ml-1 rounded-md border border-rose-900 px-2 py-1 text-xs text-rose-300 hover:bg-rose-950/50 disabled:opacity-50"
                            >
                              USUŃ
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-5 flex justify-end border-t border-slate-800 pt-4">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800 disabled:opacity-50"
          >
            ZAMKNIJ
          </button>
        </div>
      </div>
    </div>
  );
}
